import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@aptsi-types';
import { MatDialog } from '@angular/material/dialog';
import { PlatformService } from '../../services/platform.service';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { ErrorDialogComponent } from 'src/app/shared/dialogs/error-dialog/error-dialog.component';
import { WorkflowNavigationService } from '../../../shared/workflow-navigation.service';

@Component({
  selector: 'app-platform-create-or-update',
  templateUrl: './platform-create-or-update.component.html',
  styleUrls: ['./platform-create-or-update.component.scss']
})
export class PlatformCreateOrUpdateComponent implements OnInit {
  isCreate = true;
  existingPlatformId: string | null = null;
  platform: Platform | null = null;
  form!: FormGroup;

  loading = false;
  saving = false;
  pageError = '';

  readonly categories = ['Cloud', 'On-Premises', 'SaaS', 'Hybrid', 'None'];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly platformService: PlatformService,
    private readonly dialog: MatDialog,
    private readonly workflowNavigation: WorkflowNavigationService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isCreate = this.route.snapshot.data['create'] === true;
    this.existingPlatformId =
      this.route.snapshot.paramMap.get('platformId') ??
      this.route.snapshot.params['platformId'] ??
      null;

    // Build the form before fetching existing values so the update response can
    // safely patch the form when it arrives.
    this.buildForm();

    if (!this.isCreate) {
      if (!this.existingPlatformId) {
        this.pageError = 'Platform ID is missing. Unable to load this platform.';
        return;
      }
      void this.fetchPlatform();
    }
  }

  get pageTitle(): string {
    return this.isCreate ? 'Add Platform' : 'Update Platform';
  }

  get pageDescription(): string {
    return this.isCreate
      ? 'Register a platform and provide the details needed to identify and classify it.'
      : 'Review and update the platform details. The platform ID remains fixed.';
  }

  private buildForm(): void {
    this.form = new FormGroup({
      platformId: new FormControl(this.existingPlatformId),
      platformName: new FormControl('', [Validators.required, Validators.maxLength(120)]),
      category: new FormControl('None', [Validators.required, this.typeValidator]),
      description: new FormControl('', [Validators.required, Validators.maxLength(1000)])
    });
  }

  async fetchPlatform(): Promise<void> {
    if (!this.existingPlatformId) {
      this.pageError = 'Platform ID is missing. Unable to load this platform.';
      return;
    }

    this.loading = true;
    this.pageError = '';

    try {
      const response: any = await this.platformService
        .getPlatform(this.existingPlatformId)
        .toPromise();

      const retrieved = response?.['Resource retrieved successfully'];
      this.platform = Array.isArray(retrieved)
        ? (retrieved[0] as Platform | undefined) ?? null
        : (response as Platform | null);

      if (!this.platform) {
        this.pageError = 'No platform details were returned. Please try again.';
        return;
      }

      this.form.patchValue({
        platformId: this.existingPlatformId,
        platformName: this.platform.name ?? '',
        category: this.platform.category ?? 'None',
        description: this.platform.description ?? ''
      });
    } catch (error: any) {
      console.error('Error fetching platform:', error);
      this.pageError = this.extractErrorMessage(error) || 'Unable to load platform details. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  typeValidator(control: AbstractControl): { [key: string]: any } | null {
    return control.value === 'None' ? { noneSelected: true } : null;
  }

  isInvalid(controlName: string): boolean {
    const control = this.form?.get(controlName);
    return Boolean(control && control.invalid && (control.touched || control.dirty));
  }

  async submit(): Promise<void> {
    if (this.saving || this.loading || !this.form) {
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.pageError = 'Review the highlighted fields and correct the validation errors.';
      return;
    }

    const value = this.form.getRawValue();
    this.pageError = '';
    this.saving = true;

    try {
      if (this.isCreate) {
        await this.platformService
          .createPlatform(
            String(value.platformName).trim(),
            String(value.description).trim(),
            String(value.category)
          )
          .toPromise();
      } else {
        const platformId = String(value.platformId ?? this.existingPlatformId ?? '').trim();
        if (!platformId) {
          this.pageError = 'Platform ID is missing. Unable to update this platform.';
          return;
        }

        await this.platformService
          .updatePlatform(
            platformId,
            String(value.platformName).trim(),
            String(value.description).trim(),
            String(value.category)
          )
          .toPromise();
      }

      this.openSuccessDialog(this.isCreate);
    } catch (error: any) {
      console.error(this.isCreate ? 'Error creating platform:' : 'Error updating platform:', error);
      this.openErrorDialog(this.extractErrorMessage(error) || 'An unexpected error occurred.');
    } finally {
      this.saving = false;
    }
  }


  cancel(): void {
    this.navigateToPlatform();
  }

  navigateToPlatform(): void {
  void this.workflowNavigation.navigateToWorkflow(
      'securityZoneManagement',
      ['platform', 'search']
    );
  }

    openSuccessDialog(isCreate: boolean): void {
      this.dialog
        .open(SuccessDialogComponent, {
          width: '380px',
          data: {
            isCreate,
            isCreateUpdate: true,
            title: 'Platform',
            message: isCreate
              ? 'Platform created successfully.'
              : 'Platform updated successfully.',
          },
        })
        .afterClosed()
        .subscribe(() => {
          this.navigateToPlatform();
        });
    }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '380px',
      data: { errorMessage }
    });
  }



  private extractErrorMessage(error: any): string {
    const candidates = [
      error?.message,
      error?.error?.message,
      error?.error?.errorMessage,
      error?.error?.details,
      error?.errorMessage
    ];
    const message = candidates.find(candidate => typeof candidate === 'string' && candidate.trim());
    return message ? String(message).trim() : '';
  }
}
